import React, { useState } from 'react';
import { Loader2, X, UploadCloud, AlertTriangle } from 'lucide-react';

const BulkImportModal = ({
    isOpen,
    onClose,
    onImport,
    isTrial,
    isImporting,
    error
}) => {
    const [tptStoreUrl, setTptStoreUrl] = useState('');

    const handleImportClick = () => {
        if (!tptStoreUrl.trim()) {
            return; // Or show a local error message
        }
        onImport(tptStoreUrl);
    };

    if (!isOpen) {
        return null;
    }

    // The backend throws a generic "deadline-exceeded" error for large stores even
    // though the import keeps running server-side and finishes successfully a bit
    // later. Rephrase it here so it reads as an expected, reassuring status rather
    // than a failure.
    const isDeadlineExceeded = /deadline.?exceeded/i.test(error || '');
    const displayError = isDeadlineExceeded
        ? "Due to the number of products in your store, this may take around 10 minutes. It'll keep running in the background — after about 10 minutes, refresh the page and check if your products have been imported. If they haven't, please contact us for support."
        : error;

    return (
        <div className="fixed inset-0 bg-slate-800 bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-4 border-b border-slate-200">
                    <h2 className="text-xl font-semibold text-sky-700 flex items-center">
                        <UploadCloud className="h-6 w-6 mr-2" />
                        Import All Products From Store
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100"
                        aria-label="Close"
                        disabled={isImporting}
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="p-6 sm:p-8 overflow-y-auto">
                    <p className="text-sm text-slate-600 mb-4">
                        Enter your main TPT store URL below. The tool will automatically find and import your product listings.
                    </p>

                    {isTrial && (
                         <div className="mb-4 p-3 bg-amber-50 border-l-4 border-amber-400 text-amber-800 rounded-r-lg text-sm">
                            <div className="flex items-start">
                                <AlertTriangle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
                                <div>
                                    <h4 className="font-semibold">Trial Account Limitation</h4>
                                    <p>As a trial user, you can import a maximum of 5 products from your store. Upgrade to a full membership to import all your products.</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div>
                        <label htmlFor="tpt-store-url" className="block text-sm font-medium text-slate-700 mb-1">
                            Your TPT Store URL
                        </label>
                        <input
                            type="url"
                            id="tpt-store-url"
                            value={tptStoreUrl}
                            onChange={(e) => setTptStoreUrl(e.target.value)}
                            placeholder="e.g., https://www.teacherspayteachers.com/Store/Your-Store-Name"
                            className="w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-sky-500"
                            disabled={isImporting}
                        />
                    </div>

                    {displayError && (
                        <p className={`text-sm mt-2 ${isDeadlineExceeded ? 'text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3' : 'text-red-600'}`}>
                            {displayError}
                        </p>
                    )}

                    <div className="mt-6 pt-6 border-t border-slate-200 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isImporting}
                            className="bg-slate-200 hover:bg-slate-300 text-slate-800 font-semibold py-2 px-4 rounded-lg shadow-sm"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleImportClick}
                            disabled={isImporting || !tptStoreUrl.trim()}
                            className="bg-sky-600 hover:bg-sky-700 disabled:bg-slate-400 text-white font-semibold py-2 px-4 rounded-lg shadow-md flex items-center justify-center transition-colors"
                        >
                            {isImporting ? (
                                <>
                                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                    Importing...
                                </>
                            ) : (
                                "Start Import"
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BulkImportModal;